import { createElement } from 'react';
import { Paperclip } from 'lucide-react';
import { PaymentStatus, PaymentProvider } from '@masters-club/types';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { EntityConfig, ColumnConfig } from '../../components/universal/types';
import type { Payment } from './types';

type PaymentRecord = Payment;

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  confirmed: { label: 'Подтверждён', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  rejected: { label: 'Отклонён', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
  refunded: { label: 'Возврат', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  partially_refunded: { label: 'Частичный возврат', className: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
};

const PROVIDER_CONFIG: Record<string, { label: string; className: string }> = {
  manual: { label: 'Ручной', className: 'bg-gray-500/10 text-gray-600 border-gray-500/20' },
  click: { label: 'Click', className: 'bg-blue-500/10 text-blue-700 border-blue-500/20' },
  payme: { label: 'Payme', className: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20' },
  stripe: { label: 'Stripe', className: 'bg-violet-500/10 text-violet-700 border-violet-500/20' },
  multicard: { label: 'Multicard', className: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20' },
  cloudpayments: { label: 'CloudPayments', className: 'bg-sky-500/10 text-sky-700 border-sky-500/20' },
};

export const paymentConfig: EntityConfig<PaymentRecord> = {
  resource: 'payments',
  label: { singular: 'Платёж', plural: 'Платежи' },
  formLayout: 'page',
  translatable: false,
  softDelete: false,
  canCreate: false,
  canEdit: false,

  columns: [
    { key: 'id', label: 'ID', sortable: true },
    {
      key: 'userId',
      label: 'Пользователь',
      sortable: true,
      render: (_, record) => {
        const user = record.user;
        if (!user) { return `#${record.userId}`; }
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
        return name || (user.username ? `@${user.username}` : `#${record.userId}`);
      },
    },
    {
      key: 'orderId',
      label: 'Заказ',
      sortable: true,
      render: (_, record) => `#${record.orderId}`,
    },
    {
      key: 'amount',
      label: 'Сумма',
      type: 'currency',
      sortable: true,
      render: (_, record) => {
        const formatted = `${new Intl.NumberFormat('ru-RU').format(Number(record.amount))} ${record.currency}`;

        if (record.fixedExchangeRate && record.fixedBaseCurrency && record.fixedAmountBase && record.fixedBaseCurrency !== record.currency) {
          const baseFormatted = `${new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: record.fixedBaseCurrency === 'UZS' ? 0 : 2,
            maximumFractionDigits: record.fixedBaseCurrency === 'UZS' ? 0 : 2,
          }).format(Number(record.fixedAmountBase))} ${record.fixedBaseCurrency}`;

          const rateFormatted = `1 ${record.fixedBaseCurrency} = ${new Intl.NumberFormat('ru-RU').format(Number(record.fixedExchangeRate))} ${record.currency}`;

          return createElement(Tooltip, null,
            createElement(TooltipTrigger, { asChild: true },
              createElement('span', { className: 'cursor-help border-b border-dotted border-current' }, formatted),
            ),
            createElement(TooltipContent, null,
              createElement('div', { className: 'flex flex-col gap-0.5 text-xs' },
                createElement('span', { className: 'font-semibold' }, baseFormatted),
                createElement('span', { className: 'text-muted-foreground' }, rateFormatted),
              ),
            ),
          );
        }

        return formatted;
      },
    },
    {
      key: 'status',
      label: 'Статус',
      type: 'enum',
      sortable: true,
      render: (_, record) => {
        const config = STATUS_CONFIG[record.status.toLowerCase()];
        return createElement(
          Badge,
          { variant: 'outline', className: cn('text-[11px] px-1.5 py-0', config?.className) },
          config?.label ?? record.status,
        );
      },
    },
    {
      key: 'provider',
      label: 'Провайдер',
      sortable: true,
      render: (_, record) => {
        const config = PROVIDER_CONFIG[record.provider.toLowerCase()];
        return createElement(
          Badge,
          { variant: 'outline', className: cn('text-[11px] px-1.5 py-0', config?.className) },
          config?.label ?? record.provider,
        );
      },
    },
    {
      key: 'providerPaymentId',
      label: 'ID транзакции',
      render: (_, record) => {
        if (!record.providerPaymentId) { return createElement('span', { className: 'text-muted-foreground text-xs' }, '—'); }
        return createElement(
          'span',
          { className: 'font-mono text-xs text-muted-foreground', title: record.providerPaymentId },
          record.providerPaymentId.length > 18 ? `${record.providerPaymentId.slice(0, 18)}…` : record.providerPaymentId,
        );
      },
    },
    {
      key: 'proofUrl',
      label: 'Чек',
      render: (_, record) => {
        const docsCount = record.documentsCount ?? 0;
        const hasLegacyProof = Boolean(record.proofUrl);
        if (docsCount === 0 && !hasLegacyProof) {
          return createElement('span', { className: 'text-muted-foreground text-xs' }, '—');
        }
        return createElement(
          'div',
          { className: 'flex items-center gap-1' },
          docsCount > 0 && createElement(
            Badge,
            { variant: 'outline', className: 'text-[11px] px-1.5 py-0 gap-1' },
            createElement(Paperclip, { className: 'h-3 w-3' }),
            String(docsCount),
          ),
          hasLegacyProof && record.proofUrl && createElement(
            Tooltip,
            null,
            createElement(TooltipTrigger, { asChild: true },
              createElement('a', {
                href: record.proofUrl,
                target: '_blank',
                rel: 'noreferrer',
                onClick: (e: React.MouseEvent) => e.stopPropagation(),
                className: 'inline-flex items-center text-blue-600 hover:text-blue-700',
              }, createElement(Paperclip, { className: 'h-3.5 w-3.5' })),
            ),
            createElement(TooltipContent, null, 'Legacy proofUrl'),
          ),
        );
      },
    },
    {
      key: 'paidAt',
      label: 'Оплачен',
      type: 'date',
      sortable: true,
      render: (_, record) => {
        if (!record.paidAt) { return createElement('span', { className: 'text-muted-foreground text-xs' }, '—'); }
        return new Date(record.paidAt).toLocaleString('ru-RU');
      },
    },
    { key: 'createdAt', label: 'Создан', type: 'date', sortable: true },
    { key: 'updatedAt', label: 'Обновлён', type: 'date', sortable: true },
  ] as ColumnConfig<PaymentRecord>[],

  filters: [
    {
      key: 'status',
      type: 'enum',
      label: 'Статус',
      options: [
        { value: PaymentStatus.PENDING, label: 'Ожидает' },
        { value: PaymentStatus.CONFIRMED, label: 'Подтверждён' },
        { value: PaymentStatus.REJECTED, label: 'Отклонён' },
        { value: PaymentStatus.REFUNDED, label: 'Возврат' },
        { value: PaymentStatus.PARTIALLY_REFUNDED, label: 'Частичный возврат' },
      ],
    },
    {
      key: 'provider',
      type: 'enum',
      label: 'Провайдер',
      options: [
        { value: PaymentProvider.MANUAL, label: 'Ручной' },
        { value: PaymentProvider.CLICK, label: 'Click' },
        { value: PaymentProvider.PAYME, label: 'Payme' },
        { value: PaymentProvider.STRIPE, label: 'Stripe' },
        { value: PaymentProvider.MULTICARD, label: 'Multicard' },
        { value: PaymentProvider.CLOUDPAYMENTS, label: 'CloudPayments' },
      ],
    },
    {
      key: 'currency',
      type: 'enum',
      label: 'Валюта',
      options: [
        { value: 'UZS', label: 'UZS' },
        { value: 'USD', label: 'USD' },
        { value: 'EUR', label: 'EUR' },
        { value: 'RUB', label: 'RUB' },
      ],
    },
    { key: 'userId', type: 'text', label: 'ID пользователя' },
    { key: 'orderId', type: 'text', label: 'ID заказа' },
    { key: 'providerPaymentId', type: 'text', label: 'ID транзакции' },
    { key: 'orderStatusConfigId', type: 'relation', label: 'Статус заказа', resource: 'order-statuses', labelField: 'name' },
    { key: 'hasProof', type: 'boolean', label: 'Есть чек' },
    { key: 'createdAt', type: 'dateRange', label: 'Дата создания' },
    { key: 'paidAt', type: 'dateRange', label: 'Дата оплаты' },
    { key: 'amount', type: 'numberRange', label: 'Сумма' },
  ],

  bulkActions: [],

  showPage: {
    sections: [
      {
        label: 'Основное',
        fields: ['id', 'amount', 'currency', 'status', 'provider', 'providerPaymentId', 'paidAt', 'createdAt'],
      },
      {
        label: 'Связи',
        fields: ['orderId', 'userId'],
      },
    ],
  },
};
